import React, { useEffect, useRef } from "react";
import * as d3 from "d3";

interface EmotionData {
  emotion: string;
  value: number;
}

interface EmotionBarChartProps {
  data: EmotionData[];
  hoveredData?: EmotionData[] | null;
  currentArtworkTitle?: string;
  hoveredArtworkTitle?: string;
  title?: string;
  width?: number;
  height?: number;
}

export default function EmotionBarChart({
  data,
  hoveredData,
  currentArtworkTitle,
  hoveredArtworkTitle,
  width = 600,
  height = 400,
}: EmotionBarChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!data || data.length === 0 || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const margin = { top: 40, right: 30, bottom: 80, left: 60 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Scales
    const xScale = d3
      .scaleBand()
      .domain(data.map((d) => d.emotion))
      .range([0, innerWidth])
      .padding(0.1);

    const yScale = d3
      .scaleLinear()
      .domain([0, 100]) // Emotion scores are 0-100
      .range([innerHeight, 0]);

    // Create bars for main data
    const bars = g
      .selectAll(".bar")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", (d) => xScale(d.emotion)!)
      .attr("width", xScale.bandwidth())
      .attr("y", (d) => yScale(d.value))
      .attr("height", (d) => innerHeight - yScale(d.value))
      .attr("fill", "#4f46e5")
      .attr("opacity", 0.85)
      .attr("rx", 2);

    // Add hover overlay bars if hoveredData exists
    if (hoveredData && hoveredData.length > 0) {
      const hoveredMap = new Map(hoveredData.map((d) => [d.emotion, d.value]));

      g.selectAll(".hover-bar")
        .data(data)
        .enter()
        .append("rect")
        .attr("class", "hover-bar")
        .attr("x", (d) => xScale(d.emotion)!)
        .attr("width", xScale.bandwidth())
        .attr("y", (d) => {
          const hoveredValue = hoveredMap.get(d.emotion);
          return hoveredValue ? yScale(hoveredValue) : yScale(0);
        })
        .attr("height", (d) => {
          const hoveredValue = hoveredMap.get(d.emotion);
          return hoveredValue ? innerHeight - yScale(hoveredValue) : 0;
        })
        .attr("fill", "#e11d48")
        .attr("opacity", 0.75)
        .attr("stroke", "#be185d")
        .attr("stroke-width", 1)
        .attr("rx", 2);
    }

    // Add value labels on bars
    g.selectAll(".label")
      .data(data)
      .enter()
      .append("text")
      .attr("class", "label")
      .attr("x", (d) => xScale(d.emotion)! + xScale.bandwidth() / 2)
      .attr("y", (d) => yScale(d.value) - 5)
      .attr("text-anchor", "middle")
      .style("font-size", "10px")
      .style("fill", "#374151")
      .text((d) => Math.round(d.value));

    // Add hover value labels if hoveredData exists
    if (hoveredData && hoveredData.length > 0) {
      const hoveredMap = new Map(hoveredData.map((d) => [d.emotion, d.value]));

      g.selectAll(".hover-label")
        .data(data)
        .enter()
        .append("text")
        .attr("class", "hover-label")
        .attr("x", (d) => xScale(d.emotion)! + xScale.bandwidth() / 2 + 15)
        .attr("y", (d) => {
          const hoveredValue = hoveredMap.get(d.emotion);
          return hoveredValue ? yScale(hoveredValue) - 5 : yScale(0) - 5;
        })
        .attr("text-anchor", "middle")
        .style("font-size", "10px")
        .style("fill", "#dc2626")
        .style("font-weight", "bold")
        .text((d) => {
          const hoveredValue = hoveredMap.get(d.emotion);
          return hoveredValue ? Math.round(hoveredValue) : "";
        });
    }

    // X-axis
    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .attr("transform", "rotate(-45)")
      .style("font-size", "12px");

    // Y-axis
    g.append("g").call(d3.axisLeft(yScale)).style("font-size", "12px");

    // Y-axis label
    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left)
      .attr("x", 0 - innerHeight / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .style("font-size", "12px")
      .style("fill", "#374151")
      .text("Score (0-100)");

    // Subtle grid lines
    g.selectAll(".grid-line")
      .data(yScale.ticks(5))
      .enter()
      .append("line")
      .attr("class", "grid-line")
      .attr("x1", 0)
      .attr("x2", innerWidth)
      .attr("y1", (d) => yScale(d))
      .attr("y2", (d) => yScale(d))
      .attr("stroke", "#e5e7eb")
      .attr("stroke-width", 0.5)
      .attr("opacity", 0.7);
  }, [data, hoveredData, width, height]);

  return (
    <div className="w-full">
      <div className="border rounded-lg bg-gray-50">
        <svg ref={svgRef} width={width} height={height} className="w-full" />
      </div>
      {hoveredData && (
        <div className="mt-2 text-sm text-gray-500 text-center">
          <div className="flex justify-center items-center gap-4 flex-wrap">
            <div className="flex items-center">
              <span className="inline-block w-3 h-3 bg-indigo-600 rounded mr-2"></span>
              <span className="max-w-48 text-left">
                {currentArtworkTitle || "Current Artwork"}
              </span>
            </div>
            <div className="flex items-center">
              <span className="inline-block w-3 h-3 bg-pink-600 rounded mr-2"></span>
              <span className="max-w-48 text-left">
                {hoveredArtworkTitle || "Hovered Artwork"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
